import React, { useEffect, useState } from 'react';
import './LORTeamBeat.css';

function formatDate(value) {
  if (!value) return 'Live league snapshot';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Live league snapshot';
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function Stat({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="lor-beat-stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function PlayerPill({ player }) {
  return (
    <span className="lor-beat-player">
      {player.position && <b>{player.position}</b>}
      {player.name}
      {player.team && <small>{player.team}</small>}
    </span>
  );
}

function TargetCard({ target }) {
  return (
    <div className="lor-beat-target">
      <div>
        <strong>{target.name}</strong>
        <span>{[target.position, target.team].filter(Boolean).join(' · ')}</span>
      </div>
      <div className="lor-beat-target-board">
        <b>Pick {target.target_pick}</b>
        <small>ADP {target.adp} · {target.confidence} fit</small>
      </div>
      <p>{target.reason}</p>
    </div>
  );
}

function LeagueProjections({ projections, methodology, updatedAt, sourceLabel, sourceWindow, sampleSize }) {
  const [expanded, setExpanded] = useState(false);
  if (!projections?.length) return null;
  const favorite = projections[0];
  const visible = expanded ? projections : projections.slice(0, 5);

  return (
    <section className="lor-beat-projections" aria-labelledby="lor-projections-heading">
      <div className="lor-beat-section-heading">
        <div>
          <span>Draft-day model · projection, not a promise</span>
          <h3 id="lor-projections-heading">League projections</h3>
        </div>
        <p>{methodology}</p>
      </div>

      <div className="lor-beat-projection-layout">
        <article className="lor-beat-favorite">
          <small>Most likely to win draft night</small>
          <div className="lor-beat-favorite-rank">01</div>
          <h4>{favorite.team_name}</h4>
          <p className="lor-beat-favorite-manager">{favorite.manager_name}</p>
          <p>{favorite.why}</p>
          <div className="lor-beat-favorite-picks">
            {(favorite.first_open_picks || []).map(pick => (
              <span key={pick.pick_no}>R{pick.round} · {pick.pick}</span>
            ))}
          </div>
        </article>

        <div className="lor-beat-projection-board">
          <div className="lor-beat-board-head">
            <span>Rank</span><span>Draft room</span><span>First open picks</span><span>Score</span>
          </div>
          {visible.map(row => (
            <div className={`lor-beat-board-row${row.rank === 1 ? ' is-favorite' : ''}`} key={row.roster_id}>
              <strong className="lor-beat-board-rank">{String(row.rank).padStart(2, '0')}</strong>
              <div className="lor-beat-board-team">
                <b>{row.team_name}</b>
                <small>{row.why}</small>
              </div>
              <div className="lor-beat-board-picks">
                {(row.first_open_picks || []).map(pick => <span key={pick.pick_no}>{pick.pick}</span>)}
              </div>
              <strong className="lor-beat-board-score">{row.score}</strong>
            </div>
          ))}
          {projections.length > 5 && (
            <button className="lor-beat-board-toggle" type="button" onClick={() => setExpanded(value => !value)}>
              {expanded ? 'Show top five' : `Show all ${projections.length} draft rooms`}
            </button>
          )}
        </div>
      </div>
      <div className="lor-beat-model-note">
        <span>Relative outlook score — not a win probability</span>
        <span>
          {sourceLabel || 'Draft market'}
          {sampleSize ? ` · ${Number(sampleSize).toLocaleString()} drafts` : ''}
          {sourceWindow ? ` · ${sourceWindow}` : ''}
          {' · '}refreshed {formatDate(updatedAt)}
        </span>
      </div>
    </section>
  );
}

function DraftMonitor({ status }) {
  if (!status?.draft_id) return null;
  const draftStatus = String(status.draft_status || 'connecting').replace('_', ' ');
  const active = status.state !== 'error' && status.state !== 'complete';
  return (
    <>
      <section className={`lor-beat-monitor${status.state === 'error' ? ' has-error' : ''}`} aria-label="Draft monitor">
        <div className="lor-beat-monitor-signal" aria-hidden="true"><span className={active ? 'is-live' : ''} /></div>
        <div>
          <small>Draft monitor</small>
          <strong>{status.state === 'complete' ? 'Final board captured' : status.state === 'error' ? 'Connection retrying' : 'Armed and watching Sleeper'}</strong>
        </div>
        <div className="lor-beat-monitor-stats">
          <span><b>{draftStatus}</b>Status</span>
          <span><b>{status.live_picks_seen || 0}</b>Live picks</span>
          <span><b>{status.completed_rounds || 0}/20</b>Rounds</span>
        </div>
        <p>{status.notifications_enabled ? 'Discord round wraps are enabled.' : 'Monitor is active; Discord delivery is unavailable.'}</p>
      </section>
      {status.team_grades?.length > 0 && (
        <section className="lor-beat-grades" aria-labelledby="lor-draft-grades-heading">
          <div className="lor-beat-section-heading">
            <div><span>Final board</span><h3 id="lor-draft-grades-heading">Official draft grades</h3></div>
            <p>Keeper-adjusted market value. Every score shows how much of the room's draft could be graded.</p>
          </div>
          <div className="lor-beat-grade-grid">
            {status.team_grades.map(row => (
              <article key={row.roster_id}>
                <div className="lor-beat-grade-rank">#{row.rank}</div>
                <div><strong>{row.team}</strong><small>{row.manager}</small></div>
                <b className="lor-beat-grade-letter">{row.grade}</b>
                <p>{row.picks_graded}/{row.picks} graded · {row.values} values · {row.reaches} reaches · avg {Number(row.average_board_delta).toFixed(1)}</p>
                {row.best_value && <small>Best value: {row.best_value.player} ({row.best_value.delta > 0 ? '+' : ''}{row.best_value.delta})</small>}
              </article>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function LeagueArticle({ article }) {
  if (!article) return null;
  return (
    <article className="lor-beat-league-feature">
      <header>
        <span className="lor-beat-article-kicker">{article.kicker}</span>
        <h1>{article.headline}</h1>
        <p className="lor-beat-article-dek">{article.dek}</p>
        <p className="lor-beat-article-byline">{article.byline}</p>
      </header>
      <div className="lor-beat-article-layout">
        <div className="lor-beat-article-copy">
          {article.paragraphs?.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          {article.standfirst && <blockquote>{article.standfirst}</blockquote>}
          {article.sections?.map(section => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs?.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </section>
          ))}
        </div>
        <aside className="lor-beat-rank-rail" aria-label="Power ranking snapshot">
          <span>THE ORDER</span>
          {(article.ranking_snapshot || []).map(row => (
            <div key={`${row.rank}-${row.team_name}`}>
              <b>{String(row.rank).padStart(2, '0')}</b>
              <p><strong>{row.team_name}</strong><small>{row.manager_name}</small><small>{row.rank_change > 0 ? `▲ ${row.rank_change}` : row.rank_change < 0 ? `▼ ${Math.abs(row.rank_change)}` : '—'} {row.movement_trigger_label || ''}</small></p>
              <em>{row.power_score !== null && row.power_score !== undefined ? Number(row.power_score).toFixed(2) : '—'}</em>
            </div>
          ))}
        </aside>
      </div>
      <footer>Analysis is tied to the ranking snapshot shown here. No anonymous sourcing, invented quotes or manufactured transactions.</footer>
    </article>
  );
}

function TeamFile({ team, edition }) {
  const [open, setOpen] = useState(false);
  const record = team.all_time || {};
  const keeperLabel = team.keepers?.some(k => k.confirmed) ? 'Keeper desk' : 'Projected keeper desk';

  return (
    <article className={`lor-beat-team${open ? ' is-open' : ''}`}>
      <div className="lor-beat-team-bar">
        <div className="lor-beat-identity">
          {team.avatar ? (
            <img src={`https://sleepercdn.com/avatars/thumbs/${team.avatar}`} alt="" />
          ) : (
            <span className="lor-beat-avatar" role="img" aria-label="Football">🏈</span>
          )}
          <div>
            <span className="lor-beat-team-name">{team.team_name}</span>
            <span className="lor-beat-manager">Manager: {team.manager_name}</span>
          </div>
        </div>
        <div className="lor-beat-rankline">
          {edition === 'weekly' && team.standings_live && team.standing?.rank && <span>Standings #{team.standing.rank}</span>}
          {team.power_rank && <span>Power #{team.power_rank} · {team.rank_change > 0 ? `▲${team.rank_change}` : team.rank_change < 0 ? `▼${Math.abs(team.rank_change)}` : '—'}</span>}
          {edition === 'predraft' && team.draft_slot && <span>Draft slot {team.draft_slot}</span>}
        </div>
      </div>

      <div className="lor-beat-story">
        <div className="lor-beat-kicker">{edition === 'predraft' ? 'Draft-day file' : 'Weekly dispatch'}</div>
        <h3>{team.headline}</h3>
        <p className="lor-beat-byline">By {team.byline}</p>
        <p className="lor-beat-lead">{team.lead}</p>
        <div className="lor-beat-stats">
          <Stat label="All-time" value={record.total_wins !== undefined ? `${record.total_wins}-${record.total_losses}` : null} />
          <Stat label="Titles" value={record.championships} />
          <Stat label="Playoffs" value={record.playoff_appearances} />
          {edition === 'weekly' && <Stat label="Points for" value={Number(team.standing?.pf || 0).toFixed(1)} />}
        </div>
        {edition === 'predraft' && team.possible_targets?.length > 0 && (
          <div className="lor-beat-target-teaser">
            <span>Names connected by the board</span>
            <p>{team.possible_targets.map(target => `${target.name} at ${target.target_pick}`).join(' · ')}</p>
          </div>
        )}
      </div>

      <button className="lor-beat-expand" type="button" onClick={() => setOpen(value => !value)} aria-expanded={open}>
        {open ? 'Close article' : 'Read full team report'} <span>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="lor-beat-notebook">
          {team.article && (
            <div className="lor-beat-team-article">
              <p className="lor-beat-team-thesis">{team.article.thesis}</p>
              {(team.article.sections || []).map(section => (
                <section key={section.heading}>
                  <h4>{section.heading}</h4>
                  {(section.paragraphs || []).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                </section>
              ))}
              <blockquote>{team.article.verdict}</blockquote>
            </div>
          )}
          {team.keepers?.length > 0 && (
            <section>
              <h4>{keeperLabel}</h4>
              <div className="lor-beat-keepers">
                {team.keepers.map(keeper => (
                  <div className="lor-beat-keeper" key={keeper.player_id}>
                    <div><strong>{keeper.name}</strong> <span>{[keeper.position, keeper.team].filter(Boolean).join(' · ')}</span></div>
                    <small>
                      {keeper.confirmed ? 'Confirmed' : 'Projected'}
                      {keeper.cost_round ? ` · Round ${keeper.cost_round} cost` : ''}
                      {keeper.surplus_rounds !== null && keeper.surplus_rounds !== undefined ? ` · ${keeper.surplus_rounds > 0 ? '+' : ''}${keeper.surplus_rounds} rounds value` : ''}
                      {keeper.injury_status ? ` · ${keeper.injury_status}` : ''}
                    </small>
                  </div>
                ))}
              </div>
            </section>
          )}

          {team.roster_core?.length > 0 && (
            <section>
              <h4>Roster names to know</h4>
              <div className="lor-beat-player-list">
                {team.roster_core.map(player => <PlayerPill key={player.player_id} player={player} />)}
              </div>
            </section>
          )}

          {edition === 'predraft' && team.possible_targets?.length > 0 && (
            <section>
              <h4>Possible draft targets</h4>
              <p className="lor-beat-target-disclaimer">Board fits, not sourced intentions. Each name is tied to an exact open pick and current 10-team 2QB ADP.</p>
              <div className="lor-beat-targets">
                {team.possible_targets.map(target => <TargetCard key={`${target.player_id}-${target.target_pick}`} target={target} />)}
              </div>
            </section>
          )}

          <section>
            <h4>What the file says</h4>
            <ul>
              {(team.reporting_notes || []).map((note, index) => <li key={index}>{note}</li>)}
            </ul>
          </section>

          <div className="lor-beat-sources">
            {(team.evidence || []).map(source => <span key={source}>{source}</span>)}
          </div>
        </div>
      )}
    </article>
  );
}

export default function LORTeamBeat({ api }) {
  const [edition, setEdition] = useState('auto');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monitor, setMonitor] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${api}/api/lor/team-beat?edition=${edition}`)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(payload => {
        setData(payload);
        setLoading(false);
      })
      .catch(() => {
        setError('The newsroom could not load this edition. Try again after the next league refresh.');
        setLoading(false);
      });
  }, [api, edition]);

  useEffect(() => {
    if (data?.edition !== 'predraft') {
      setMonitor(null);
      return undefined;
    }
    let active = true;
    let unsupported = false;
    const refreshMonitor = () => fetch(`${api}/api/lor/draft-monitor/status`)
      .then(response => {
        if (response.status === 404) unsupported = true;
        return response.ok ? response.json() : null;
      })
      .then(payload => { if (active && payload) setMonitor(payload); })
      .catch(() => {});
    refreshMonitor();
    const timer = setInterval(() => { if (!unsupported) refreshMonitor(); }, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [api, data?.edition]);

  const activeEdition = data?.edition || (edition === 'weekly' ? 'weekly' : 'predraft');
  const span = data?.history_span;

  return (
    <div className="lor-beat-page">
      <header className="lor-beat-masthead">
        <div>
          <div className="lor-beat-brand">THE LOR INSIDER</div>
          <h2>{data?.title || 'Team Beat Report'}</h2>
          <p>{data?.deck || 'League-wide reporting and one beat file for every franchise.'}</p>
        </div>
        <div className="lor-beat-editions" aria-label="Report edition">
          <button className={activeEdition === 'predraft' ? 'active' : ''} onClick={() => setEdition('predraft')}>Draft Day</button>
          <button className={activeEdition === 'weekly' ? 'active' : ''} onClick={() => setEdition('weekly')}>Weekly</button>
        </div>
      </header>

      {loading && <div className="lor-loading">The newsroom is reviewing the league file…</div>}
      {error && <div className="lor-empty">{error}</div>}

      {data && !loading && (
        <>
          <div className="lor-beat-dateline">
            <span>Filed {formatDate(data.generated_at)}</span>
            <span>Data through {formatDate(data.data_through)}</span>
            {data.edition === 'predraft' && data.draft_start_time && (
              <span>Draft starts {formatDate(Number(data.draft_start_time) * 1000)}</span>
            )}
            {span?.seasons_studied > 0 && <span>{span.seasons_studied} verified seasons studied · {span.first_season}–{span.last_season}</span>}
            {data.declaration_status?.all_confirmed && (
              <span className="lor-beat-confirmed">✓ {data.declaration_status.confirmed_managers}/{data.declaration_status.total_managers} managers submitted · all filed keepers confirmed</span>
            )}
          </div>

          <LeagueArticle article={data.league_article} />

          {(data.edition === 'predraft' || monitor?.team_grades?.length > 0) && <DraftMonitor status={monitor} />}

          <section className="lor-beat-wire">
            <div className="lor-beat-wire-label"><span>LIVE</span> League wire</div>
            <div className="lor-beat-wire-grid">
              {(data.wire || []).map((item, index) => (
                <article key={`${item.kicker}-${index}`}>
                  <small>{item.kicker}</small>
                  <h3>{item.headline}</h3>
                  <p>{item.detail}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="lor-beat-cadence" aria-label="Weekly publication schedule">
            {(data.publication_schedule || []).map(slot => (
              <div key={slot.day}><strong>{slot.day} · {slot.time}</strong><span>{slot.edition}</span><small>{slot.focus}</small></div>
            ))}
          </section>

          {data.edition === 'predraft' && (
            <LeagueProjections
              projections={data.draft_projections}
              methodology={data.projection_methodology}
              updatedAt={data.adp_updated_at}
              sourceLabel={data.adp_source_label}
              sourceWindow={data.adp_source_window}
              sampleSize={data.adp_sample_size}
            />
          )}

          <div className="lor-beat-section-heading">
            <div>
              <span>All 10 franchises</span>
              <h3>From the team beats</h3>
            </div>
            <p>{data.methodology}</p>
          </div>

          <div className="lor-beat-team-grid">
            {(data.teams || []).map(team => <TeamFile key={team.roster_id} team={team} edition={data.edition} />)}
          </div>
        </>
      )}
    </div>
  );
}
